import { useEffect, useState } from 'react'
import { getMediaProxyToken, getMediaProxyUrlWithToken, fetchMediaFileBlob } from '../api'
import { useI18n } from '../i18n'

/**
 * Модальный просмотр медиа: фото или видео.
 * Поддерживает локальные файлы (source=local) и прокси CDN.
 */
export default function MediaViewer({ open, onClose, modelId, item }) {
  const { t } = useI18n()
  const [mediaSrc, setMediaSrc] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const isVideo = item?.type === 'video'
  const isAudio = item?.type === 'audio'
  const isLocal = item?.source === 'local' && item?.id
  const fullUrl = item?.files?.full?.url ?? item?.files?.preview?.url ?? item?.files?.squarePreview?.url ?? item?.files?.thumb?.url

  useEffect(() => {
    if (!open || !modelId) return

    // В просмотрщике показываем только полный контент (media_path), не превью
    const hasFullMedia = isLocal ? item.media_path : fullUrl
    if (!hasFullMedia) {
      if (isLocal && item?.id) {
        setLoading(true)
        setError('')
        setMediaSrc(null)
      }
      return
    }

    setError('')
    setLoading(true)

    if (isLocal) {
      fetchMediaFileBlob(modelId, item.id, 'full')
        .then((url) => {
          if (url) {
            setMediaSrc(url)
          } else setError(t('media.viewer.fileNotUploaded'))
        })
        .catch(() => setError(t('media.viewer.loadError')))
        .finally(() => setLoading(false))
      return () => setMediaSrc(null)
    }

    getMediaProxyToken(modelId, fullUrl)
      .then((token) => {
        if (token) {
          const url = getMediaProxyUrlWithToken(modelId, fullUrl, token)
          setMediaSrc(url)
        } else {
          setError(t('media.viewer.accessError'))
        }
      })
      .catch(() => setError(t('media.viewer.loadError')))
      .finally(() => setLoading(false))

    return () => setMediaSrc(null)
  }, [open, modelId, fullUrl, isLocal, item?.id, item?.media_path])

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="media-viewer-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр медиа"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <button
        type="button"
        className="media-viewer-close"
        onClick={onClose}
        aria-label={t('media.viewer.aria.close')}
      >
        ×
      </button>
      <div className="media-viewer-content" onClick={(e) => e.stopPropagation()}>
        {loading && <p className="media-viewer-loading">{t('media.viewer.loading')}</p>}
        {error && <p className="media-viewer-error">{error}</p>}
        {isVideo && mediaSrc && !loading && (
          <video
            src={mediaSrc}
            controls
            autoPlay
            playsInline
            className="media-viewer-video"
          />
        )}
        {isAudio && mediaSrc && !loading && (
          <audio src={mediaSrc} controls autoPlay className="media-viewer-audio" />
        )}
        {!isVideo && !isAudio && mediaSrc && !loading && (
          <img
            src={mediaSrc}
            alt=""
            className="media-viewer-image"
            onError={() => setError(t('media.viewer.imageError'))}
          />
        )}
      </div>
    </div>
  )
}
